import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
  OnChanges,
  ChangeDetectorRef,
  AfterViewChecked,
  ElementRef
} from '@angular/core';
import {FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors} from '@angular/forms';
import { EquineService } from '../equine.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { requireEquineQuoteId, resolveEquinePreviousPolicyId, scrollToFirstInvalidControl } from '../equine-utils';
import {formatDate} from '@angular/common';
import {Observable} from 'rxjs';
import {map, startWith} from 'rxjs/operators';
import { maxDate } from 'ngx-custom-validators/src/app/max-date/validator';

@Component({
  selector: 'app-equine-additional-horse-details',
  templateUrl: './equine-additional-horse-details.component.html',
  styleUrls: ['./equine-additional-horse-details.component.css', '../../quote.component.css']
})
export class EquineAdditionalHorseDetailsComponent implements OnInit, OnChanges, AfterViewChecked {
  @Input() staticData;
  @Input() modelObject: any = {};
  @Output() modelObjectUpdated = new EventEmitter<any>();
  @Output() GoToNextTab = new EventEmitter<void>();
  @Output() GoToPrevTab = new EventEmitter<void>();

  formFields: FormGroup;
  submitted = false;
  saving = false;
  
  // Static data arrays
  heights: any = [];
  breeds: any = [];
  colors: any = [];
  sexes: any = [];

  private static readonly BLOODSTOCK_BREED_PATTERN = /^(thoroughbred|standardbred|arab|trotter)$/i;
  isBloodstockCover: boolean = false;
  
  // Filtered options for autocomplete
  filteredBreeds: Observable<any[]>;
  filteredColors: Observable<any[]>;
  
  maxVettingDate: Date = new Date();
  isYesSelection = EquineAdditionalHorseDetailsComponent.isYesSelection;

  constructor(
    private formBuilder: FormBuilder,
    private equineService: EquineService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private el: ElementRef
  ) {}

  public ngOnInit(): void {
    this.initializeForm();
    this.generateHeightOptions();
    this.loadStaticData();
    this.setupAutocompleteFilters();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.modelObject && this.modelObject && Object.keys(this.modelObject).length > 0) {
      this.generateHeightOptions();
      this.loadStaticData();
      this.setupAutocompleteFilters();
      this.patchFormValues();
    }
    if (changes.staticData && this.staticData) {
      this.loadStaticData();
      this.setupAutocompleteFilters();
      // Re-patch when static data arrives so breed/color/sex IDs resolve to display values
      if (this.modelObject?.horse_details) {
        this.patchFormValues();
      }
    }
  }

  private static readonly yesNoRequired = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    return value == 1 || value == 2  ? null : { required: true };
  };

  static isYesSelection(value: unknown): boolean {
    return value == 2
  }

  private static isYesNoSelection(value: unknown): boolean {
    return value == 1 || value == 2
  }

  public patchFormValues() {
    const horseDetails = this.modelObject.horse_details;

    if (!horseDetails) return;

    if (horseDetails.height) {
      this.formFields.get('height').setValue(horseDetails.height);
    }

    // Breed: always resolve to full object so input shows breed name, not ID
    const breedId = horseDetails.breed_id ?? horseDetails.breed?.id;
    if (breedId) {
      const breed = this.breeds.find(b => (b.id === breedId || b.value === breedId || String(b.id) === String(breedId)));
      if (breed) {
        this.formFields.get('breed_id').setValue(breed);
      } else {
        this.formFields.get('breed_id').reset('');
      } 
    }

    // Colour: always resolve to full object so input shows colour name, not ID
    const colorId = horseDetails.color_id ?? horseDetails.color?.id;
    if (colorId) {
      const color = this.colors.find(c => (c.id === colorId || c.value === colorId || String(c.id) === String(colorId)));
      if (color) {
        this.formFields.get('color_id').setValue(color);
      } else if (horseDetails.color_name || horseDetails.horse_color?.color) {
        const name = horseDetails.color_name || horseDetails.horse_color?.color;
        this.formFields.get('color_id').setValue({ id: colorId, color: name });
      } else {
        this.formFields.get('color_id').setValue({ id: colorId, color: String(colorId) });
      }
    }

    // Sex: backend may send gender_id (uuid) or gender (0/1). Resolve to option id so mat-select shows title, not ID
    const sexId = horseDetails.gender_id ?? horseDetails.sex_id;
    if (sexId && this.sexes.length) {
      const sex = this.sexes.find(s => (s.id === sexId || String(s.id) === String(sexId)));
      if (sex) {
        this.formFields.get('sex_id').setValue(sex.id);
      }
    } else if (horseDetails.gender !== undefined && horseDetails.gender !== null && this.sexes.length) {
      const g = Number(horseDetails.gender);
      const sex = this.sexes.find(s => s.value === g || s.value === String(g) || (g === 0 && (s.title || '').toLowerCase() === 'male') || (g === 1 && (s.title || '').toLowerCase() === 'female'));
      if (sex) {
        this.formFields.get('sex_id').setValue(sex.id);
      }
    }
    if (horseDetails.passport !== undefined) {
      this.formFields.get('passport_number').setValue(horseDetails.passport);
    }
    if (horseDetails.date_of_vetting) {
      this.formFields.get('date_of_vetting').setValue(new Date(horseDetails.date_of_vetting));
    }

    if (EquineAdditionalHorseDetailsComponent.isYesNoSelection(horseDetails.is_horse_vetted)) {
      this.formFields.get('is_horse_vetted').setValue(horseDetails.is_horse_vetted);
    } else {
      this.formFields.get('is_horse_vetted').setValue(null);
    }
    if (EquineAdditionalHorseDetailsComponent.isYesNoSelection(horseDetails.is_insured_by_you)) {
      this.formFields.get('is_currently_insured').setValue(horseDetails.is_insured_by_you);
    } else {
      this.formFields.get('is_currently_insured').setValue(null);
    }
  }

  public ngAfterViewChecked() {
    this.cdr.detectChanges();
  }

  public initializeForm() {
    this.formFields = this.formBuilder.group({
      height: ['', Validators.required],
      breed_id: ['', Validators.required],
      color_id: ['', Validators.required],
      sex_id: ['', Validators.required],
      passport_number: [''],
      is_horse_vetted: [null, EquineAdditionalHorseDetailsComponent.yesNoRequired],
      date_of_vetting: [''],
      is_currently_insured: [null, EquineAdditionalHorseDetailsComponent.yesNoRequired]
    });

    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 30);
    this.maxVettingDate = maxDate;

    // Set up conditional validation for date_of_vetting
    this.formFields.get('is_horse_vetted').valueChanges.subscribe(value => {
      const dateOfVettingControl = this.formFields.get('date_of_vetting');
      if (EquineAdditionalHorseDetailsComponent.isYesSelection(value)) {
        dateOfVettingControl.setValidators([Validators.required]);
      } else {
        dateOfVettingControl.clearValidators();
        dateOfVettingControl.setValue('');
      }
      dateOfVettingControl.updateValueAndValidity();
    });
  }

  public loadStaticData(): void {
    if (!this.staticData) { return; }

    const allBreeds = this.staticData.horse_breeds ?? [];
    this.colors = this.staticData.horse_colors ?? [];
    this.sexes = this.staticData.horse_gender ?? [];

    this.detectBloodstockCover();

    this.breeds = this.isBloodstockCover
      ? allBreeds.filter(b => EquineAdditionalHorseDetailsComponent.BLOODSTOCK_BREED_PATTERN.test((b.breed || '').trim()))
      : allBreeds;
  }

  private detectBloodstockCover(): void {
    const coverTypeId = this.modelObject?.policy_cover_details?.cover_type_id
      ?? this.modelObject?.cover_type_id;
    if (!coverTypeId || !this.staticData?.cover_types) {
      this.isBloodstockCover = false;
      return;
    }
    const coverType = this.staticData.cover_types.find(
      ct => ct.id === coverTypeId || String(ct.id) === String(coverTypeId)
    );
    this.isBloodstockCover = coverType
      ? (coverType.cover_type_name || '').toLowerCase().includes('bloodstock')
      : false;
  }

  /**
   * Generate height options for the height select dropdown
   */
  public generateHeightOptions(): void {
    const minHand = 5;
    const maxHand = 22.3;
    const heightOptions: string[] = [];
    
    for (let i = minHand; i <= maxHand; i++) {
      heightOptions.push(`${i}.0`);
      
      // We add the inch increments unless we've hit the top of the range
      if (i < maxHand) {
        heightOptions.push(`${i}.1`);
        heightOptions.push(`${i}.2`);
        heightOptions.push(`${i}.3`);
      }
    } 

    this.heights = heightOptions;
  }

  public setupAutocompleteFilters() {
    // Setup breed filter
    this.filteredBreeds = this.formFields.get('breed_id').valueChanges.pipe(
      startWith(''),
      map(value => {
        if (!value) {
          return this.breeds.slice();
        }
        const searchValue = typeof value === 'string' ? value : (value?.breed || '');
        return searchValue ? this._filterBreeds(searchValue) : this.breeds.slice();
      })
    );

    // Setup color filter
    this.filteredColors = this.formFields.get('color_id').valueChanges.pipe(
      startWith(''),
      map(value => {
        if (!value) {
          return this.colors.slice();
        }
        const searchValue = typeof value === 'string' ? value : (value?.color || value?.name || value?.label || '');
        return searchValue ? this._filterColors(searchValue) : this.colors.slice();
      })
    );
  }

  private _filterBreeds(value: string): any[] {
    const filterValue = value.toLowerCase();
    return this.breeds.filter(breed => {
      const breedName = (breed.breed || '').toLowerCase();
      return breedName.includes(filterValue);
    });
  }

  private _filterColors(value: string): any[] {
    const filterValue = value.toLowerCase();
    return this.colors.filter(color => {
      const colorName = (color.color || color.name || color.label || '').toLowerCase();
      return colorName.includes(filterValue);
    });
  }

  displayBreed(breed: any): string {
    if (breed == null) return '';
    if (typeof breed === 'object' && breed.breed) return breed.breed;
    const id = typeof breed === 'string' ? breed : (breed?.id ?? breed?.value);
    if (id && this.breeds.length) {
      const found = this.breeds.find(b => b.id === id || b.value === id || String(b.id) === String(id));
      return found ? (found.breed || '') : '';
    }
    return typeof breed === 'string' ? breed : '';
  }

  displayColor(color: any): string {
    if (color == null) return '';
    if (typeof color === 'object') {
      const name = color.color || color.name || color.label || color.value;
      if (name) return name;
    }
    const id = typeof color === 'string' ? color : (color?.id ?? color?.value);
    if (id && this.colors.length) {
      const found = this.colors.find(c => c.id === id || c.value === id || String(c.id) === String(id));
      return found ? (found.color || found.name || found.label || found.value || '') : '';
    }
    return typeof color === 'string' ? color : '';
  }

  get f(): { [key: string]: AbstractControl } {
    return this.formFields.controls;
  }

  /** Save and optionally go to next tab. goToNext: true = Save & Continue, false = Save Indicative Quote only. */
  onSubmit(goToNext = true) {
    this.submitted = true;

    const quoteId = requireEquineQuoteId(this.modelObject);
    if (!quoteId) {
      this._snackBar.open('No quote ID found. Please save Quote Details first.', 'Close', { duration: 3000 });
      return;
    }

    if (this.formFields.invalid) {
      this.formFields.markAllAsTouched();
      scrollToFirstInvalidControl(this.formFields, this.el.nativeElement);
      return;
    }

    const formValue = this.formFields.getRawValue();
    
    // Get existing horse_Details or create default structure
    const existingHorseDetails = this.modelObject.horse_details || {};
    
    // Extract IDs from objects if needed
    const breedId = (formValue.breed_id && typeof formValue.breed_id === 'object') 
      ? (formValue.breed_id.id || formValue.breed_id.value) 
      : (formValue.breed_id || existingHorseDetails.breed_id || null);
    
    const colorId = (formValue.color_id && typeof formValue.color_id === 'object') 
      ? (formValue.color_id.id || formValue.color_id.value) 
      : (formValue.color_id || existingHorseDetails.color_id || null);

    const m = this.modelObject;
    const pcd = m?.policy_cover_details && Object.keys(m.policy_cover_details).length
      ? m.policy_cover_details
      : {
          cover_type_id: m?.cover_type_id,
          class_of_use_id: m?.horse_class_id,
          sub_class_of_use_id: m?.horse_class_sub_id,
          bloodstock_id: m?.blood_stock_id,
          is_lou_selected: m?.is_lou_selected,
          is_elective_surgery: m?.is_elective_surgery,
          is_disposal_euthansia: m?.is_disposal_euthansia,
          is_cover_for_permanent_lou: m?.is_cover_for_permanent_lou,
          lou_activity_id: m?.lou_activity_id,
          is_vet_fee_selected: m?.is_vet_fee_selected,
          vet_fee_cover_type: m?.vet_fee_cover_type,
          vet_fee_id: m?.vet_fee_id,
          vet_fee_excess_id: m?.vet_fee_excess_id,
          vet_fee_co_insurance_id: m?.vet_fee_co_insurance_id,
          is_livery_hispitalization: m?.is_livery_hispitalization,
          is_life_saving_surgery_selected: m?.is_life_saving_surgery_selected
        };

    // Structure payload with all required fields and defaults (matching template structure)
    const data: any = {
      ...this.modelObject,
      id: quoteId,
      transaction_type: this.modelObject.type,
      has_form_changed: this.formFields.dirty,
      policy_cover_details: pcd,
      previous_policy_id: resolveEquinePreviousPolicyId(this.modelObject),
      horse_details: {
        id: existingHorseDetails.id || null,
        stable_name: existingHorseDetails.stable_name || existingHorseDetails.name || '',
        name: existingHorseDetails.name || existingHorseDetails.stable_name || '',
        passport_name: existingHorseDetails.passport_name || '',
        acquired_date: existingHorseDetails.acquired_date || existingHorseDetails.horse_purchase_date || null,
        horse_purchase_date: existingHorseDetails.horse_purchase_date || existingHorseDetails.acquired_date || null,
        ownership_status_id: existingHorseDetails.ownership_status_id || '',
        purchase_price: existingHorseDetails.purchase_price ?? null,
        height: formValue.height || existingHorseDetails.height || undefined,
        value: existingHorseDetails.value ?? existingHorseDetails.purchase_price ?? null,
        birth_year: existingHorseDetails.birth_year || existingHorseDetails.horse_birth_year || null,
        horse_birth_year: existingHorseDetails.horse_birth_year || existingHorseDetails.birth_year || null,
        breed_id: breedId,
        color_id: colorId,
        gender_id: formValue.sex_id || existingHorseDetails.gender_id ,
        age: existingHorseDetails.age,
        passport: formValue.passport_number || existingHorseDetails.passport_number ,
        is_horse_vetted: formValue.is_horse_vetted,
        date_of_vetting: formValue.date_of_vetting ? formatDate(formValue.date_of_vetting, 'yyyy-MM-dd', 'en') : null,
        is_insured_by_you: formValue.is_currently_insured
      }
    };

    const prevPolicyId = this.modelObject.previous_policy_id;
    const txnType = this.modelObject.type || this.modelObject.transaction_type;
    const origTxType = this.modelObject.original_transaction_type;
    this.saving = true;
    this.equineService.saveQuote(data)
      .subscribe(
        (response: any) => {
          this.saving = false;
          if (response.success) {
            this._snackBar.open('Additional horse details saved successfully', 'Close', {
              duration: 3000
            });
            const updated = response.result;
            if (prevPolicyId) updated.previous_policy_id = prevPolicyId;
            if (txnType) updated.type = txnType;
            if (origTxType) updated.original_transaction_type = origTxType;
            this.modelObjectUpdated.emit(updated);
            this.submitted = false;
            if (goToNext) {
              this.GoToNextTab.emit();
            }
          } else {
            this._snackBar.open(response.message || 'Failed to save additional horse details', 'Close', {
              duration: 5000
            });
          }
        },
        (error) => {
          this.saving = false;
          this.submitted = false;
          this._snackBar.open('Error saving additional horse details', 'Close', {
            duration: 3000
          });
        }
      );
  }
}
